import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { AppDataState, AppId, AppThunkAPIType, unknownError } from "../../../../app/appTypes";
import { RootState } from "../../../../app/store";
import { getWithAuth, postWithAuth, putWithAuth } from "../../../../http"

export interface SysMailFooter {
    id: AppId,
    name: string;
    footer: string;
    isDefault: boolean;
}

export type DraftSysMailFooter = Omit<SysMailFooter, "id">;

const adapter = createEntityAdapter<SysMailFooter>({
    selectId: (mailFooter) => mailFooter.id,
})

export type SysMailFootersState = EntityState<SysMailFooter> & { state: AppDataState }

const initialState: SysMailFootersState = adapter.getInitialState({
    state: { type: "empty" },
})

export const loadSysMailFooters = createAsyncThunk<SysMailFooter[], void, AppThunkAPIType>("sysMailFooters/load", async (context, { getState, requestId, dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({ url: `api/sys/mail-footers` }))
    const { payload } = result

    if (getWithAuth.fulfilled.match(result)) {
        return payload as SysMailFooter[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }

})

export const createSysMailFooter = createAsyncThunk<SysMailFooter, DraftSysMailFooter, AppThunkAPIType>(
    "sysMailFooters/new",
    async (mailFooter: DraftSysMailFooter, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ url: "api/sys/mail-footers", payload: mailFooter }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as SysMailFooter
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateSysMailFooter = createAsyncThunk<SysMailFooter, SysMailFooter, AppThunkAPIType>(
    "sysMailFooters/edit",
    async (mailFooter: SysMailFooter, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({ url: `api/sys/mail-footers/${mailFooter.id}`, payload: mailFooter }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return mailFooter
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const sysMailFootersSlice = createSlice({
    name: "sysMailFooters",
    initialState,
    reducers: {
        clearRequirementsTypesSlice: (state) => {
            adapter.removeAll(state)
            state.state = { type: "empty" }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createSysMailFooter.fulfilled, (state, action) => {
            state = adapter.addOne(state, action.payload)
        });

        builder.addCase(updateSysMailFooter.fulfilled, (state, action) => {
            const { id } = action.payload
            const changes: Omit<SysMailFooter, "id"> = action.payload
            state = adapter.updateOne(state, {
                id,
                changes,
            });
        });

        builder.addCase(loadSysMailFooters.rejected, (state, action) => {
            if (state.state.type === "loading" && state.state.requestId === action.meta.requestId) {
                state.state = {
                    type: "error",
                    error: action.payload ?? unknownError(),
                }
            }
        })

        builder.addCase(loadSysMailFooters.pending, (state, action) => {
            if (state.state.type === "empty" || state.state.type === "loaded") {
                state.state = {
                    type: "loading",
                    requestId: action.meta.requestId,
                }
            }
        })


        builder.addCase(loadSysMailFooters.fulfilled, (state, action) => {
            if (state.state.type === "loading" && state.state.requestId === action.meta.requestId) {
                adapter.setAll(state, action.payload)
                state.state = { type: "loaded" }
            }
        })
    },
})

export const selectSysMailFooters = (state: RootState): SysMailFootersState => state.sysMailFooters

export const
    { selectAll: selectAllSysMailFooters
        , selectById: selectSysMailFooterById
        , selectTotal: selectTotalSysMailFooters
        , selectEntities: selectMailSysFootersEntities
    } = adapter.getSelectors<RootState>(selectSysMailFooters)

export default sysMailFootersSlice.reducer;
